import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Card from '../Card';

export interface DetailRow {
  id: string;
  to?: string; // drill-down route; if set the whole row is a <Link>
  title: string;
  subtitle?: string;
  badges?: React.ReactNode; // status/priority
  meta?: React.ReactNode; // right-aligned (due date, budget, % complete)
}

export interface DetailChildListProps {
  title: string; // "Projects", "Milestones", "Tasks"
  rows: DetailRow[];
  emptyMessage: string;
}

const rowClasses = 'flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg transition-all';

const DetailChildList: React.FC<DetailChildListProps> = ({ title, rows, emptyMessage }) => {
  return (
    <Card variant="elevated">
      <Card.Header title={`${title} (${rows.length})`} />
      <Card.Body>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">{emptyMessage}</p>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => {
              const content = (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white truncate">{row.title}</div>
                    {row.subtitle && (
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">{row.subtitle}</div>
                    )}
                  </div>
                  {row.badges && <div className="flex flex-wrap gap-2 items-center">{row.badges}</div>}
                  {row.meta && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{row.meta}</div>
                  )}
                  {row.to && <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                </>
              );

              return row.to ? (
                <Link
                  key={row.id}
                  to={row.to}
                  className={`${rowClasses} cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 hover:shadow-md`}
                >
                  {content}
                </Link>
              ) : (
                <div key={row.id} className={rowClasses}>
                  {content}
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DetailChildList;
